import { useState } from "react";
import type { Trade, TradeTag } from "wasp/entities";
import { deleteTrade, getTradeCustomFieldDefinitions, updateTrade, useAction, useQuery } from "wasp/client/operations";
import { Button } from "../client/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../client/components/ui/dialog";
import { useToast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import { trackTradeDeleted } from "../client/utils/posthog";
import { parseCustomFieldValue } from "./customFieldsUtils";
import TradeForm, { type TradeFormData, initialTradeFormData } from "./TradeForm";

/**
 * Modifier et supprimer UN trade : formulaire, validations, appels serveur,
 * toasts et suivi. Sorti du journal Advanced (TradingJournalPage) pour que le
 * panneau de trade du Lite propose exactement les mêmes actions.
 *
 * Renvoie `openEdit(trade)`, `openDelete(trade)` et `dialogs` (à monter une fois).
 */
export function useTradeEditing({ onChanged }: { onChanged?: () => void } = {}) {
  const t = useTranslation();
  const { toast } = useToast();
  const updateTradeFn = useAction(updateTrade);
  const deleteTradeFn = useAction(deleteTrade);
  const { data: customFields = [] } = useQuery(getTradeCustomFieldDefinitions);

  const [editingTrade, setEditingTrade] = useState<Trade | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState<TradeFormData>(initialTradeFormData);
  const [tradeToDelete, setTradeToDelete] = useState<Trade | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const openEdit = (trade: Trade) => {
    setEditingTrade(trade);
    const tradeDate = new Date(trade.date);
    const dateStr = `${tradeDate.getFullYear()}-${String(tradeDate.getMonth() + 1).padStart(2, "0")}-${String(tradeDate.getDate()).padStart(2, "0")}`;
    setFormData({
      date: dateStr,
      symbol: trade.symbol,
      type: trade.type as "long" | "short",
      status: (trade.status as "open" | "closed") || "closed",
      entryPrice: trade.entryPrice.toString(),
      exitPrice: trade.exitPrice?.toString() || "",
      quantity: trade.quantity.toString(),
      pnl: trade.pnl?.toString() || "",
      commission: trade.commission.toString(),
      swaps: (trade as any).swaps?.toString() || "",
      notes: trade.notes || "",
      setupRating: (trade as any).setupRating ?? null,
      tagIds: (trade as any).tags?.map((tg: TradeTag) => tg.id) || [],
      accountId: (trade as any).account?.id || (trade as any).accountId || null,
      customFields: (((trade as any).TradeCustomFieldValue || []) as any[])
        .map((cv: any) => {
          const fieldDef = (customFields as any[]).find((f: any) => f.id === cv.field?.id || f.id === cv.fieldId);
          if (!fieldDef) return null;
          const value = parseCustomFieldValue(
            {
              id: fieldDef.id,
              name: fieldDef.name,
              label: fieldDef.label,
              type: (fieldDef.type as string) as "text" | "number" | "date" | "boolean" | "tags",
              isRequired: fieldDef.isRequired,
            },
            cv,
          );
          return { fieldId: fieldDef.id, value };
        })
        .filter((cf: any): cf is { fieldId: string; value: any } => cf !== null),
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingTrade) return;

    if (!formData.date) {
      toast({ title: t.common.error, description: t.validation.fieldRequired.replace("{field}", t.journal.date), variant: "destructive" });
      return;
    }
    if (!formData.symbol || formData.symbol.trim() === "") {
      toast({ title: t.common.error, description: t.validation.fieldRequired.replace("{field}", t.journal.symbol), variant: "destructive" });
      return;
    }
    if (!formData.entryPrice || isNaN(parseFloat(formData.entryPrice)) || parseFloat(formData.entryPrice) <= 0) {
      toast({ title: t.common.error, description: t.validation.fieldRequired.replace("{field}", t.journal.entryPrice), variant: "destructive" });
      return;
    }
    if (!formData.quantity || isNaN(parseFloat(formData.quantity)) || parseFloat(formData.quantity) <= 0) {
      toast({ title: t.common.error, description: t.validation.fieldRequired.replace("{field}", t.journal.quantity), variant: "destructive" });
      return;
    }

    try {
      await updateTradeFn({
        id: editingTrade.id,
        date: new Date(formData.date),
        symbol: formData.symbol,
        type: formData.type,
        status: formData.status,
        entryPrice: parseFloat(formData.entryPrice),
        exitPrice: formData.exitPrice ? parseFloat(formData.exitPrice) : null,
        quantity: parseFloat(formData.quantity),
        pnl: formData.pnl && formData.status === "closed" ? parseFloat(formData.pnl) : null,
        commission: parseFloat(formData.commission) || 0,
        swaps: formData.swaps ? parseFloat(formData.swaps) : null,
        notes: formData.notes || null,
        setupRating: formData.setupRating,
        tagIds: formData.tagIds,
        accountId: formData.accountId || null,
        customFields: formData.customFields,
      });
      toast({
        title: t.journal.tradeUpdated,
        description: t.journal.tradeUpdatedDescription,
      });
      setIsEditDialogOpen(false);
      setEditingTrade(null);
      setFormData(initialTradeFormData);
      onChanged?.();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.journal.cannotUpdate,
        variant: "destructive",
      });
    }
  };

  const openDelete = (trade: Trade) => {
    setTradeToDelete(trade);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!tradeToDelete) return;
    setIsDeleteConfirmOpen(false);
    try {
      await deleteTradeFn({ id: tradeToDelete.id });
      trackTradeDeleted();
      toast({
        title: t.journal.tradeDeleted,
        description: t.journal.tradeDeletedDescription,
      });
      setTradeToDelete(null);
      onChanged?.();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.journal.cannotDelete,
        variant: "destructive",
      });
    }
  };

  const dialogs = (
    <>
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.journal.confirmDelete}</DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>{t.common.cancel}</Button>
            <Button variant="destructive" onClick={confirmDelete}>{t.journal.deleteTrade}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.journal.editTrade}</DialogTitle>
            <DialogDescription>{t.journal.subtitle}</DialogDescription>
          </DialogHeader>
          <TradeForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleUpdate}
            onCancel={() => {
              setIsEditDialogOpen(false);
              setEditingTrade(null);
              setFormData(initialTradeFormData);
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );

  return { openEdit, openDelete, dialogs };
}
